import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ClientAuthProvider, useClientAuth } from './context/ClientAuthContext';
import { Toaster } from './components/ui/sonner';

// Accountant Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import LetterTemplates from './pages/LetterTemplates';
import Invoices from './pages/Invoices';
import BankStatements from './pages/BankStatements';
import BankFeeds from './pages/BankFeeds';
import Matching from './pages/Matching';
import RecurringInvoices from './pages/RecurringInvoices';
import FormTemplates from './pages/FormTemplates';
import Layout from './components/Layout';

// Client Portal Pages
import ClientLogin from './pages/portal/ClientLogin';
import ClientSetup from './pages/portal/ClientSetup';
import ClientDashboard from './pages/portal/ClientDashboard';
import ClientDocuments from './pages/portal/ClientDocuments';
import ClientUpload from './pages/portal/ClientUpload';
import ClientSignature from './pages/portal/ClientSignature';
import ClientInvoices from './pages/portal/ClientInvoices';
import ClientFormsAndDocs from './pages/portal/ClientFormsAndDocs';
import ClientLayout from './components/ClientLayout';

import './App.css';

// Accountant Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Accountant Public Route
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Client Portal Protected Route
const ClientProtectedRoute = ({ children }) => {
  const { client, loading } = useClientAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!client) {
    return <Navigate to="/portal/login" replace />;
  }

  return children;
};

// Client Portal Public Route
const ClientPublicRoute = ({ children }) => {
  const { client, loading } = useClientAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (client) {
    return <Navigate to="/portal/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Accountant Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="clients" element={<Clients />} />
        <Route path="clients/:id" element={<ClientDetail />} />
        <Route path="letter-templates" element={<LetterTemplates />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="bank-statements" element={<BankStatements />} />
        <Route path="bank-feeds" element={<BankFeeds />} />
        <Route path="matching" element={<Matching />} />
        <Route path="recurring-invoices" element={<RecurringInvoices />} />
        <Route path="form-templates" element={<FormTemplates />} />
      </Route>

      {/* Client Portal Routes */}
      <Route path="/portal/login" element={<ClientPublicRoute><ClientLogin /></ClientPublicRoute>} />
      <Route path="/client-setup" element={<ClientSetup />} />
      <Route path="/portal" element={<ClientProtectedRoute><ClientLayout /></ClientProtectedRoute>}>
        <Route index element={<Navigate to="/portal/dashboard" replace />} />
        <Route path="dashboard" element={<ClientDashboard />} />
        <Route path="documents" element={<ClientDocuments />} />
        <Route path="upload" element={<ClientUpload />} />
        <Route path="invoices" element={<ClientInvoices />} />
        <Route path="forms" element={<ClientFormsAndDocs />} />
        <Route path="signature" element={<ClientSignature />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ClientAuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </ClientAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
